import { sortStr } from '@utils/sorting/non-ascii';
import Field, { IField, IFieldApiModel } from './Field.model';

export const fieldTypeOptions = <const>[
  'text',
  'date',
  'select',
  'url',
  'contact',
  'phone',
  'email',
  'description',
  null,
];
export type FieldTypeOptions = typeof fieldTypeOptions[number];

export interface ICustomField {
  id: string;
  name: string;
  type: FieldTypeOptions;
  studioConfigurationId: string;
  isUnique: boolean;
  isSearchable: boolean;
  fieldContents: IField[];
  selectableValues: string[];
  selectableValuesObject: ISelectableValuesApiModel[];
  createdAt?: Date;
  updatedAt?: Date;
  showOnOnboard?: boolean;
  defaultValue?: string;
  description?: string;
}
export interface ISelectableValuesApiModel {
  id: string;
  value: string;
  customFieldId: string;
}
export interface ICustomFieldApiModel {
  id: string;
  name: string;
  type: FieldTypeOptions;
  studioConfigurationId: string;
  isUnique: boolean;
  isSearchable: boolean;
  // studioConfiguration : StudioConfigurationApiModel
  fieldContents: IFieldApiModel[];
  selectableValues: ISelectableValuesApiModel[];
  createdAt?: string;
  description?: string;
  updatedAt?: string;
  showOnOnboard?: boolean;
  defaultValue?: string;
}

export default class CustomField implements ICustomField {
  public readonly id: string;

  public readonly name: string;

  public readonly description?: string;

  public readonly studioConfigurationId: string;

  public readonly type: FieldTypeOptions;

  public readonly isUnique: boolean;

  public readonly isSearchable: boolean;

  public readonly fieldContents: IField[];

  public readonly selectableValues: string[];

  public readonly selectableValuesObject: ISelectableValuesApiModel[];

  public readonly createdAt: Date | undefined;

  public readonly updatedAt: Date | undefined;

  public readonly showOnOnboard: boolean | undefined;

  public readonly defaultValue: string | undefined;

  constructor(
    id: string,
    name: string,
    studioConfigurationId: string,
    type: FieldTypeOptions,
    isUnique: boolean,
    isSearchable: boolean,
    fieldContents: IField[],
    selectableValues: string[],
    selectableValuesObject: ISelectableValuesApiModel[],
    createdAt?: Date,
    description?: string,
    updatedAt?: Date,
    showOnOnboard?: boolean,
    defaultValue?: string,
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.studioConfigurationId = studioConfigurationId;
    this.selectableValuesObject = selectableValuesObject;
    this.type = type;
    this.isUnique = isUnique;
    this.isSearchable = isSearchable;
    this.fieldContents = fieldContents;
    this.selectableValues = selectableValues;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.showOnOnboard = showOnOnboard;
    this.defaultValue = defaultValue;
  }

  static fromApiModel(apiModel: ICustomFieldApiModel): ICustomField {
    const {
      id,
      name,
      description,
      type,
      isUnique,
      isSearchable,
      studioConfigurationId,
      showOnOnboard,
      defaultValue,
    } = apiModel;

    const createdAt = apiModel.createdAt
      ? new Date(Date.parse(apiModel.createdAt))
      : undefined;
    const updatedAt = apiModel.updatedAt
      ? new Date(Date.parse(apiModel.updatedAt))
      : undefined;

    const fieldContents = apiModel.fieldContents
      ? Field.arrayFromApiModel(apiModel.fieldContents)
      : [];
    const _selectableValues = apiModel.selectableValues
      ? apiModel.selectableValues.map(selectValue => selectValue.value)
      : [];
    const selectableValues = [..._selectableValues].sort(sortStr);
    return new CustomField(
      id,
      name,
      studioConfigurationId,
      type,
      isUnique,
      isSearchable,
      fieldContents,
      selectableValues,
      apiModel.selectableValues,
      createdAt,
      description,
      updatedAt,
      showOnOnboard,
      defaultValue,
    );
  }

  static arrayFromApiModel(arrayApiModel: ICustomFieldApiModel[]) {
    const array = arrayApiModel.map(apiModel =>
      CustomField.fromApiModel(apiModel),
    );

    return array;
  }
}
